import { API_BASE_URL } from '@/constants';

interface ErrorReportPayload {
  level?: 'ERROR' | 'FATAL' | 'WARN' | 'CRITICAL';
  category?: string;
  message: string;
  route?: string;
  userAgent?: string;
  error?: {
    name?: string;
    message?: string;
    stack?: string;
  };
  metadata?: Record<string, any>;
}

class ErrorReporter {
  private initialized = false;
  private recentReports = new Set<string>();

  /**
   * Initializes global window error and unhandled rejection listeners
   */
  public init() {
    if (this.initialized || typeof window === 'undefined') return;
    this.initialized = true;

    window.addEventListener('error', (event) => {
      this.report({
        level: 'ERROR',
        category: 'FRONTEND_RUNTIME',
        message: event.message || 'Uncaught window error',
        route: window.location.pathname + window.location.search,
        userAgent: navigator.userAgent,
        error: {
          name: event.error?.name || 'Error',
          message: event.message || 'Window Error',
          stack: event.error?.stack,
        },
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
          ? reason
          : 'Unhandled Promise Rejection';

      this.report({
        level: 'ERROR',
        category: 'FRONTEND_RUNTIME',
        message: `Unhandled Rejection: ${message}`,
        route: window.location.pathname + window.location.search,
        userAgent: navigator.userAgent,
        error: {
          name: reason instanceof Error ? reason.name : 'UnhandledRejection',
          message,
          stack: reason instanceof Error ? reason.stack : undefined,
        },
        metadata: {
          reason: typeof reason === 'object' ? JSON.stringify(reason) : String(reason),
        },
      });
    });
  }

  /**
   * Reports an explicit runtime error or exception to the central observability service
   */
  public report(payload: ErrorReportPayload) {
    try {
      const dedupeKey = `${payload.message}-${payload.route}`;
      if (this.recentReports.has(dedupeKey)) {
        return; // Suppress client flood
      }
      this.recentReports.add(dedupeKey);
      setTimeout(() => this.recentReports.delete(dedupeKey), 5000);

      const endpoint = `${API_BASE_URL}/platform/logs/client-log`;

      // Use non-blocking fetch / sendBeacon
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(endpoint, blob);
      } else {
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // Failsafe
    }
  }
}

export const errorReporter = new ErrorReporter();
